import {
  ArrowGo,
  Calendar,
  Check,
  Checked,
  Heart,
  HeartFilled,
  Pin,
  Share,
  UserFilled,
  Users,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import React from "react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import { PageHeader } from "@/components/page-header"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import {
  eventAttendeePath,
  eventAttendeesPath,
  eventPath,
  eventsEditPath,
  eventsPath,
  userPath,
} from "@/routes"

interface EventShowProps {
  event: {
    id: number
    title: string
    description: string
    date: string
    time: string
    end_date?: string
    end_time?: string
    location?: {
      name: string
      address: string
      neighborhood: string
    }
    venue_name?: string
    address?: string
    neighborhood?: {
      name: string
    }
    organizer: {
      id: number
      name: string
      verified: boolean
      avatar_url?: string | null
    }
    category: {
      name: string
      emoji: string
    }
    price: number
    max_capacity?: number
    current_attendees: number
    image_url?: string | null
    tags?: string[]
    is_attending: boolean
    is_organizer?: boolean
    user_attendance_id?: number | null
    featured: boolean
    is_favorited?: boolean
    favorites_count?: number
  }
  related_events: {
    id: number
    title: string
    date: string
    time: string
    venue_name?: string
    featured: boolean
  }[]
  attendees: {
    id: number
    name: string
    avatar_url?: string | null
  }[]
  can_edit?: boolean
}

export default function EventShow({
  event,
  related_events = [],
  attendees = [],
  can_edit = false,
}: EventShowProps) {
  const [isLoading, setIsLoading] = React.useState(false)
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("event", event.id)
  const storedCount = getStoreCount("event", event.id)
  const actualFavorited = storedFavorite ?? event.is_favorited
  const actualFavoritesCount = storedCount ?? event.favorites_count ?? 0

  // Check if event has started
  const hasEventStarted = () => {
    const now = new Date()
    let eventDateTime: Date

    // If date already includes time (ISO format), use it directly
    if (event.date.includes("T") && event.date.includes("Z")) {
      eventDateTime = new Date(event.date)
    } else {
      // Otherwise, combine date and time
      eventDateTime = new Date(`${event.date} ${event.time}`)
    }

    return now >= eventDateTime
  }
  const hasCapacity =
    typeof event.max_capacity === "number" && event.max_capacity > 0
  const spotsRemaining = hasCapacity
    ? (event.max_capacity ?? 0) - event.current_attendees
    : 0
  const isFullyBooked = hasCapacity ? spotsRemaining <= 0 : false

  const handleAttend = () => {
    setIsLoading(true)
    router.post(
      `/eventos/${event.id}/asistentes`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          // Recargamos para hidratar user_attendance_id y el estado desde el servidor
          router.visit(eventPath(event.id), { preserveScroll: true })
        },
        onError: () => {
          setIsLoading(false)
        },
      },
    )
  }

  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("event", event.id, newValue)
    setStoreCount("event", event.id, newCount)

    router.post(
      `${eventPath(event.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("event", event.id, !newValue)
          setStoreCount(
            "event",
            event.id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const handleShare = async () => {
    try {
      const url = window.location.href
      const title = event?.title ?? "Evento"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const handleDelete = () => {
    if (!confirm("¿Eliminar este evento? Esta acción no se puede deshacer."))
      return
    router.delete(eventPath(event.id), {
      preserveScroll: false,
      onSuccess: () => {
        router.visit(eventsPath())
      },
    })
  }

  const handleCancelAttendance = () => {
    // El backend ignora el ID y borra por Current.user, así que usamos un fallback
    const attendeeId = event.user_attendance_id ?? "me"
    setIsLoading(true)
    router.delete(eventAttendeePath(event.id, attendeeId), {
      preserveScroll: true,
      onSuccess: () => {
        // Recargamos para asegurar estado consistente
        router.visit(eventPath(event.id), { preserveScroll: true })
      },
      onError: () => {
        setIsLoading(false)
      },
    })
  }
  const formatPrice = (price: number) => {
    if (price === 0) return "Gratis"
    const number = new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price)

    return `€\u00A0${number}`
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Fecha no disponible"

    const date = new Date(dateString)
    if (isNaN(date.getTime())) return "Fecha no válida"

    return new Intl.DateTimeFormat("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date)
  }
  const formatEventDate = (dateStr: string) => {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return ""

    const parts = new Intl.DateTimeFormat("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date)

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? ""

    let weekday = get("weekday")
    weekday = weekday.trim()
    weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    if (!weekday.endsWith(".")) weekday = `${weekday}.`

    const day = get("day")
    let month = get("month")
    month = month.replace(/\./g, "").toLowerCase()

    const time = `${get("hour")}:${get("minute")}`

    return `${weekday} ${day} ${month} • ${time}hs.`
  }
  // Moved to component state
  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      onEdit={() => router.visit(eventsEditPath(event.id))}
      onDelete={handleDelete}
    >
      <Head title={event.title} />

      <div className="mx-auto max-w-6xl space-y-4 py-4 md:space-y-6 md:p-6">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={false}
            showFavorite={false}
            editHref={eventsEditPath(event.id)}
            onDelete={handleDelete}
          />
        </div>

        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <button
              onClick={() => window.history.back()}
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Eventos
              </div>
            </button>
          </div>
          {event.category && (
            <span className="text-card-foreground bg-card h-[23px] rounded-[6px] px-[10px] py-[3px] text-[12px] leading-[0.24px] font-medium tracking-[0.24px]">
              {event.category.name}
            </span>
          )}
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-chat-5 mt-[14px] text-[21px] leading-[28px] font-semibold tracking-[0px] break-words">
              {event.title}
            </h1>
            <ContentActionsMenu
              contentType="Event"
              contentId={event.id}
              authorId={event.organizer.id}
              authorName={event.organizer.name}
            />
          </div>
          <div className="flex items-center gap-[14px]">
            {event.price > 0 ? (
              <span className="text-primary bg-card h-[31px] rounded-[10px] px-[8px] py-[3px] text-[18px] leading-[27px] font-semibold tracking-[0.18px] whitespace-nowrap">
                € {event.price}
              </span>
            ) : (
              <span className="bg-secondary inline-flex h-[17px] w-fit items-center justify-center rounded-[8px] px-[7px] py-[2px] text-[10px] leading-[14px] tracking-[0.57px] text-white">
                GRATIS
              </span>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="fill-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                {formatEventDate(event.date)}
              </span>
            </div>
          </div>

          <div className="text-muted-foreground mt-[11px] flex flex-wrap items-center gap-[16px] text-sm">
            <div className="flex items-center">
              <Users className="fill-muted-foreground mr-1 h-4.5 w-auto" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.20px]">
                {event.current_attendees} asistentes
              </span>
            </div>
            {hasCapacity && (
              <div className="flex items-center gap-1">
                <div className="bg-active h-[8px] w-[8px] rounded-full" />
                <span className="text-card-foreground h-[17px] text-[12px] leading-[18px] font-medium tracking-[0.24px]">
                  {isFullyBooked
                    ? "Completo"
                    : `${spotsRemaining} plazas disponibles`}
                </span>
              </div>
            )}
            {(event.venue_name ?? event.neighborhood?.name) && (
              <>
                <div className="text-card-foreground flex h-[17px] items-center text-[12px] leading-[18px] font-medium tracking-[0.24px]">
                  <Pin className="fill-muted-foreground mr-1 size-4" />
                  <span>{event.venue_name ?? event.neighborhood?.name}</span>
                </div>
              </>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-[14px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
                authAction="register"
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>
        </div>

        {/* Event Info */}
        {event.featured && (
          <div className="m-4">
            <Badge variant="outline">⭐ Destacado</Badge>
          </div>
        )}

        {/* Event Image */}
        {event.image_url && (
          <div className="bg-images flex aspect-video overflow-hidden">
            <img
              src={event.image_url}
              alt={event.title}
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <div className="grid gap-4 md:gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="min-w-0 lg:col-span-2">
            {/* Description */}
            {event.description && (
              <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Descripción del evento
                </CardTitle>
                <div
                  className="prose prose-sm ext-[13px] text-chart-5 max-w-none overflow-hidden leading-[20px] tracking-[0.2px] break-words"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}
            {/* Organizer Info */}
            <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
              <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                Organizador
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="bg-card flex h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-full">
                  <Avatar className="h-[54px] w-[54px]">
                    <AvatarImage
                      src={event.organizer.avatar_url ?? undefined}
                      alt={event.organizer.name}
                    />
                    <AvatarFallback>
                      <UserFilled className="size-9 fill-white" />
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <Link
                    href={userPath(event.organizer.id)}
                    className="flex items-center gap-2"
                  >
                    <span className="text-chart-5 text-[14px] leading-[21px] font-semibold tracking-[0px]">
                      {event.organizer.name}
                    </span>
                    {event.organizer.verified && (
                      <Check className="fill-primary h-4.5 w-4.5" />
                    )}
                  </Link>
                  <Link
                    href={userPath(event.organizer.id)}
                    className="text-chart-3 h-[21px] text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                  >
                    Ver Perfil
                  </Link>
                </div>
              </div>
            </div>

            {/* Attendees */}
            {attendees.length > 0 && (
              <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
                <div className="flex justify-between">
                  <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                    {event.current_attendees} Asistentes
                  </CardTitle>
                  {can_edit && hasEventStarted() && (
                    <Link href={eventAttendeesPath(event.id)}>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Checked className="fill-primary h-3 w-3" />
                        Check-in
                      </Button>
                    </Link>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center -space-x-2">
                    {attendees.slice(0, 5).map((attendee, index) => (
                      <div
                        key={attendee.id}
                        className="bg-card relative flex h-[44px] w-[44px] flex-shrink-0 items-center justify-center rounded-full"
                        style={{ zIndex: index }}
                      >
                        <Avatar className="h-[40px] w-[40px]">
                          <AvatarImage
                            src={attendee.avatar_url ?? undefined}
                            alt={attendee.name}
                          />
                          <AvatarFallback>
                            <UserFilled className="size-9 fill-white" />
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    ))}
                  </div>
                  {attendees.length > 5 && (
                    <Link
                      href={eventAttendeesPath(event.id)}
                      className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]"
                    >
                      + {attendees.length - 5}
                      <span className="text-chart-3 ml-1 text-[12px] leading-[18px] font-medium tracking-[0.18px]">
                        Ver Asistentes
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          {!event.is_organizer && (
            <div className="bg-card min-w-0 pb-[20px]">
              <div className="w-full px-4 pt-[20px]">
                <div className="flex items-center justify-center gap-[14px] py-[16px]">
                  {event.price > 0 ? (
                    <span className="text-primary h-[36px] text-[26px] leading-[39px] font-bold tracking-[0.26px] whitespace-nowrap">
                      € {event.price}
                    </span>
                  ) : (
                    <span className="bg-secondary inline-flex h-[17px] w-fit items-center justify-center rounded-[8px] px-[7px] py-[2px] text-[10px] leading-[14px] tracking-[0.57px] text-white">
                      GRATIS
                    </span>
                  )}

                  {event.max_capacity && (
                    <div className="flex items-center gap-1">
                      <div className="bg-active h-[8px] w-[8px] rounded-full" />
                      <span className="text-card-foreground h-[17px] text-[12px] leading-[18px] font-medium tracking-[0.24px]">
                        {isFullyBooked
                          ? "Completo"
                          : `${event.max_capacity - event.current_attendees} plazas disponibles`}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {event.is_attending && (
                    <div className="text-muted-foreground text-center text-sm">
                      Ya estás asistiendo
                    </div>
                  )}

                  {event.price > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-chart-5 text-[15px] leading-[30px] font-semibold tracking-[0.23px]">
                        Total
                      </span>
                      <div className="bg-chart-2 mx-1 mt-1 h-[2px] w-full" />
                      <span className="text-primary text-[15px] leading-[30px] font-bold tracking-[0.23px]">
                        {formatPrice(event.price)}
                      </span>
                    </div>
                  )}

                  {event.is_attending ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      size="lg"
                      onClick={handleCancelAttendance}
                      disabled={isLoading}
                    >
                      {isLoading ? "Cancelando..." : "Cancelar asistencia"}
                    </Button>
                  ) : (
                    <AuthButton
                      className="w-full"
                      size="lg"
                      onClick={handleAttend}
                      disabled={isLoading || (hasCapacity && isFullyBooked)}
                      authAction="register"
                    >
                      {isLoading
                        ? "Procesando..."
                        : isFullyBooked
                          ? "Cupo lleno"
                          : "Asistir"}
                    </AuthButton>
                  )}
                </div>
                {hasCapacity ? (
                  <div className="text-muted-foreground mt-[23px] flex items-center justify-center text-[13px] leading-[20px] tracking-[0.2px]">
                    <span className="text-muted-foreground">Capacidad:</span>
                    <span className="text-chart-5 font-semibold">
                      &nbsp; {event.current_attendees}/{event.max_capacity}
                    </span>
                  </div>
                ) : (
                  <div className="text-muted-foreground mt-[23px] flex items-center justify-center text-[13px] leading-[20px] tracking-[0.2px]">
                    <span className="text-muted-foreground">Asistentes:</span>
                    <span className="text-chart-5 font-semibold">
                      {event.current_attendees}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Event Details */}
          <div className="w-full">
            <CardTitle className="text-chart-5 px-4 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
              Detalles del evento
            </CardTitle>
            <div className="space-y-4 px-4">
              <div className="flex items-start gap-3">
                <div className="bg-card flex h-[32px] w-[32px] items-center justify-center rounded-full p-1">
                  <Calendar className="fill-primary h-full w-full" />
                </div>
                <div>
                  <div className="text-chart-5 text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                    {formatDate(event.date)}
                  </div>
                  <div className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                    {event.time}
                  </div>
                  {event.end_date && (
                    <div className="text-muted-foreground mt-1 text-xs">
                      Finaliza: {formatDate(event.end_date)}
                      {event.end_time ? ` · ${event.end_time}` : ""}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-card flex h-[32px] w-[32px] items-center justify-center rounded-full p-1">
                  <Pin className="fill-primary h-full w-full" />
                </div>
                <div>
                  <div className="text-chart-5 text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                    {event.location?.name ??
                      event.venue_name ??
                      "Ubicación por confirmar"}
                  </div>
                  <div className="text-muted-foreground text-[13px] leading-[20px] tracking-[0.2px]">
                    {event.location?.address ?? event.address}
                  </div>
                  <div className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                    {event.location?.neighborhood ?? event.neighborhood?.name}
                  </div>
                  <button
                    type="button"
                    className="text-chart-3 flex h-[21px] items-center gap-1 text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                    // onClick={(e) => {
                    //   e.preventDefault()
                    //   e.stopPropagation()
                    //   const query = [item.venue_name, item.address].filter(Boolean).join(' ')
                    //   const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
                    //   window.open(url, '_blank', 'noopener,noreferrer')
                    // }}
                  >
                    Ver en Maps
                    <ArrowGo className="fill-chart-3 h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Events */}
        {related_events.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Eventos relacionados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related_events.slice(0, 3).map((relatedEvent) => (
                  <Link key={relatedEvent.id} href={eventPath(relatedEvent.id)}>
                    <Card className="cursor-pointer transition-all duration-200 hover:shadow-md">
                      <CardContent className="p-4">
                        <div className="mb-1 flex items-center gap-2">
                          {relatedEvent.featured && (
                            <Badge className="bg-primary/10 text-primary">
                              ⭐
                            </Badge>
                          )}
                          <h4 className="text-foreground line-clamp-2 text-sm font-semibold break-words sm:text-base">
                            {relatedEvent.title}
                          </h4>
                        </div>
                        <div className="text-muted-foreground mt-2 flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(relatedEvent.date)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
